import React, { memo } from "react";
import PropTypes from "prop-types";
import { Button as MuiButton, CircularProgress } from "@mui/material";

/**
 * Custom Button Component
 * Uses Material UI Button with theme-based styling
 * Maps legacy className-based variants to Material UI props
 */
const Button = memo((props) => {
    const {
        text,
        children,
        loading = false,
        disabled = false,
        onClick,
        type = "button",
        className = "",
        style,
        ...rest
    } = props;

    // Map legacy class names to Material UI variants and colors
    let variant = "contained";
    let color = "primary";
    const muiProps = {};
    const isActive = className.includes("active");

    if (className) {
        // Parse className to determine variant and color
        if (className.includes("btn-primary")) {
            variant = "contained";
            color = "primary";
        } else if (className.includes("btn-secondary")) {
            variant = "outlined";
            color = "primary";
        } else if (className.includes("btn-success")) {
            variant = "contained";
            color = "success";
        } else if (className.includes("btn-delete") || className.includes("btn-danger")) {
            variant = "outlined";
            color = "error";
        } else if (className.includes("btn-warning")) {
            variant = "outlined";
            color = "warning";
        } else if (className.includes("btn-link")) {
            variant = "text";
            color = "primary";
        }

        // Handle full width
        if (className.includes("w-100")) {
            muiProps.fullWidth = true;
        }
    }

    // Build sx prop for active state styling
    const sxStyles = {
        ...(isActive && {
            bgcolor: "primary.main !important",
            color: "white !important",
            borderColor: "primary.main !important",
            "&:hover": {
                bgcolor: "primary.dark !important",
                borderColor: "primary.dark !important",
            },
            "&.MuiButton-outlined": {
                bgcolor: "primary.main !important",
                color: "white !important",
            },
        }),
    };

    return (
        <MuiButton
            type={type}
            disabled={disabled || loading}
            onClick={onClick}
            variant={variant}
            color={color}
            className={className}
            style={style}
            sx={sxStyles}
            {...muiProps}
            {...rest}
        >
            {loading ? (
                <CircularProgress
                    size={20}
                    sx={{
                        color: variant === "outlined" ? color : "inherit",
                    }}
                />
            ) : (
                children || text
            )}
        </MuiButton>
    );
});

Button.propTypes = {
    text: PropTypes.string,
    children: PropTypes.node,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    type: PropTypes.oneOf(["button", "submit", "reset"]),
    className: PropTypes.string,
    style: PropTypes.object,
};

Button.displayName = "Button";

export default Button;
